<template>
	<div>
		<b-card no-body class="mb-2">
			<div class="d-flex flex-wrap justify-content-between align-items-start p-2">
				<div>
					<h4 class="mb-25">Modules &amp; Permissions</h4>
					<p class="text-muted mb-0">
						Turn features on or off per company — disabled items disappear immediately for every user on web and mobile.
					</p>
				</div>
				<div style="min-width: 280px" class="mt-1 mt-md-0">
					<label class="font-weight-bold small text-uppercase text-muted mb-50" for="company-select">
						Company
					</label>
					<v-select
						id="company-select"
						v-model="selectedCompany"
						label="company_name"
						placeholder="Search for a company…"
						:options="companyList"
						:reduce="(e) => e.id"
						:filterable="false"
						:loading="loadingCompanies"
						@search="onCompanySearch"
						@input="onCompanyChange"
					/>
				</div>
			</div>
		</b-card>

		<!-- Nothing selected yet -->
		<b-card v-if="!selectedCompany" class="text-center py-4">
			<feather-icon icon="BriefcaseIcon" size="36" class="text-muted mb-1" />
			<h5>Select a company to get started</h5>
			<p class="text-muted mb-0">Choose a company above to manage its modules and production types.</p>
		</b-card>

		<template v-else>
			<!-- Edit / Delete rights (companies.can_edit) -->
			<b-card no-body>
				<div class="d-flex justify-content-between align-items-center p-2">
					<div>
						<h5 class="mb-0">Edit &amp; Delete Rights</h5>
						<small class="text-muted">
							When off, nobody at this company — not even the owner — can create, edit, or delete data entries.
						</small>
					</div>
					<b-form-checkbox
						switch
						class="ml-2"
						aria-label="Toggle edit and delete rights"
						:checked="canEdit"
						:disabled="pendingEditRights"
						@change="toggleEditRights"
					/>
				</div>
			</b-card>

			<!-- Modules -->
			<b-card no-body class="mt-2">
				<div class="d-flex justify-content-between align-items-center p-2 pb-0">
					<h5 class="mb-0">Modules</h5>
					<b-badge v-if="moduleLists.length > 0" pill variant="light-primary">
						{{ enabledModuleCount }} / {{ moduleLists.length }} enabled
					</b-badge>
				</div>

				<div v-if="loadingModules" class="p-2">
					<b-skeleton-wrapper :loading="true">
						<template #loading>
							<div class="module-grid">
								<b-skeleton v-for="i in 6" :key="i" height="64px" width="100%" class="mb-0" />
							</div>
						</template>
					</b-skeleton-wrapper>
				</div>

				<div v-else-if="moduleLists.length > 0" class="module-grid p-2 pt-1">
					<div
						v-for="modules in moduleLists"
						:key="modules.id"
						class="module-item"
						:class="{ 'module-item--active': checkModule(modules.id) }"
					>
						<div class="module-item__icon">
							<feather-icon :icon="moduleIcon(modules.module_name)" size="18" />
						</div>
						<div class="module-item__label">{{ modules.module_name }}</div>
						<b-form-checkbox
							switch
							class="module-item__switch"
							:aria-label="`Toggle ${modules.module_name}`"
							:checked="checkModule(modules.id)"
							:disabled="pendingModuleId === modules.id"
							@change="addModules(modules.id)"
						/>
					</div>
				</div>

				<div v-else class="d-flex flex-column align-items-center py-4">
					<b-img
						:src="require('@/assets/images/svg/infographics/no_data_infographic.svg')"
						width="140"
					/>
					<h5 class="mt-2 text-secondary">No modules found</h5>
				</div>
			</b-card>

			<!-- Production Types -->
			<b-card no-body class="mt-2">
				<div class="p-2 pb-0">
					<div class="d-flex justify-content-between align-items-center">
						<h5 class="mb-0">Production Types</h5>
						<b-badge v-if="productionTypeList.length > 0" pill variant="light-primary">
							{{ enabledProductionTypeCount === 0 ? 'All allowed' : `${enabledProductionTypeCount} restricted` }}
						</b-badge>
					</div>
					<div class="alert alert-warning py-50 px-1 mt-1 mb-0 d-flex align-items-start">
						<feather-icon icon="InfoIcon" size="16" class="mt-25 mr-50 flex-shrink-0" />
						<small>
							Leave everything off to allow <strong>all</strong> production types (unrestricted).
							Turning any one on restricts this company to only the types enabled here — everywhere, on both web and the mobile app.
						</small>
					</div>
				</div>

				<div v-if="loadingProductionTypes" class="p-2">
					<b-skeleton-wrapper :loading="true">
						<template #loading>
							<div class="module-grid">
								<b-skeleton v-for="i in 3" :key="i" height="64px" width="100%" class="mb-0" />
							</div>
						</template>
					</b-skeleton-wrapper>
				</div>

				<div v-else-if="productionTypeList.length > 0" class="module-grid p-2 pt-1">
					<div
						v-for="productionType in productionTypeList"
						:key="productionType.id"
						class="module-item"
						:class="{ 'module-item--active': checkProductionType(productionType.id) }"
					>
						<div class="module-item__icon">
							<feather-icon icon="FeatherIcon" size="18" />
						</div>
						<div class="module-item__label">{{ productionType.name }}</div>
						<b-form-checkbox
							switch
							class="module-item__switch"
							:aria-label="`Toggle ${productionType.name}`"
							:checked="checkProductionType(productionType.id)"
							:disabled="pendingProductionTypeId === productionType.id"
							@change="addProductionType(productionType.id)"
						/>
					</div>
				</div>

				<div v-else class="d-flex flex-column align-items-center py-4">
					<b-img
						:src="require('@/assets/images/svg/infographics/no_data_infographic.svg')"
						width="140"
					/>
					<h5 class="mt-2 text-secondary">No production types found</h5>
				</div>
			</b-card>
		</template>
	</div>
</template>

<script>
import {
	BCard,
	BImg,
	BFormCheckbox,
	BBadge,
	BSkeleton,
	BSkeletonWrapper,
} from "bootstrap-vue";

import axiosIns from "@/libs/axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

// Best-effort icon per known module — anything unmapped falls back to a
// generic settings icon rather than guessing, since the module list is
// backend-driven and new modules can be added without a matching icon here.
const MODULE_ICON_MAP = {
	tasks: "CheckSquareIcon",
	vaccination: "ActivityIcon",
	market_prices: "TrendingUpIcon",
	vet_consult: "HeartIcon",
	disease_alerts: "AlertTriangleIcon",
	chick_quality: "AwardIcon",
	hygicult_mmf: "ShieldIcon",
	master_entry: "EditIcon",
	company: "BriefcaseIcon",
	users: "UsersIcon",
	purchase: "ShoppingCartIcon",
	inventory: "PackageIcon",
	expense_entry: "DollarSignIcon",
	action_list: "ListIcon",
	alarm: "BellIcon",
	reports: "FileTextIcon",
	sale: "TagIcon",
	roles: "KeyIcon",
	permission: "LockIcon",
	dashboard: "GridIcon",
	analysis: "PieChartIcon",
	farm: "MapPinIcon",
	company_purchase: "ShoppingBagIcon",
	company_inventory: "ArchiveIcon",
	supply_to_farm: "TruckIcon",
	data_entry: "EditIcon",
	bird_sale: "FeatherIcon",
	bookings: "ShoppingCartIcon",
	health_scores: "ActivityIcon",
	benchmarking: "BarChart2Icon",
	alert_settings: "BellIcon",
};

export default {
	components: {
		BCard,
		BImg,
		BFormCheckbox,
		BBadge,
		BSkeleton,
		BSkeletonWrapper,
	},
	data() {
		return {
			companyList: [],
			selectedCompany: null,
			moduleLists: [],
			assigned_modules: [],
			productionTypeList: [],
			assigned_production_types: [],
			loadingModules: false,
			loadingProductionTypes: false,
			pendingModuleId: null,
			pendingProductionTypeId: null,
			loadingCompanies: false,
			canEdit: true,
			pendingEditRights: false,
		};
	},
	computed: {
		enabledModuleCount() {
			return this.assigned_modules.length;
		},
		enabledProductionTypeCount() {
			return this.assigned_production_types.length;
		},
	},
	created() {
		this.getCompanyList();
	},
	methods: {
		moduleIcon(moduleName) {
			const key = (moduleName || "").toLowerCase().trim().replace(/\s+/g, "_");
			return MODULE_ICON_MAP[key] || "SettingsIcon";
		},
		onCompanyChange() {
			this.getModules();
			this.getProductionTypes();
			this.getEditRights();
		},
		// NOTE: GET web/companies/edit-rights (CompanyEditRightsController::index)
		// is dead/unreachable — routes/api/admin.php registers
		// Route::apiResource('companies', ...) earlier in the same prefix
		// group, so GET companies/{company} matches "edit-rights" as a
		// company id first and 404s before the dedicated route is ever
		// reached (documented at that route's registration in admin.php).
		// Instead, can_edit is read straight from the companyList entries
		// already fetched by getCompanyList() (web/getAllCompanyList selects
		// the full company row — can_edit included, not hidden/guarded on
		// the Company model) — the selected company is always present there
		// since v-select's options come from that same array. The POST
		// toggle endpoint used below is unaffected by the routing collision.
		getEditRights() {
			const match = (this.companyList || []).find((c) => c.id == this.selectedCompany);
			this.canEdit = match ? !!match.can_edit : true;
		},
		toggleEditRights() {
			if (this.pendingEditRights) return;
			this.pendingEditRights = true;
			const nextValue = !this.canEdit;
			axiosIns
				.post(`web/companies/${this.selectedCompany}/edit-rights`, { can_edit: nextValue })
				.then((res) => {
					this.canEdit = nextValue;
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: { icon: "CoffeeIcon", variant: "success", text: res.data.message || "Edit rights updated" },
					});
				})
				.catch((error) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text: error.response?.data?.message || "Failed to update edit rights",
						},
					});
				})
				.finally(() => {
					this.pendingEditRights = false;
				});
		},
		getModules() {
			this.loadingModules = true;
			axiosIns
				.get(`web/all-modules?companyId=${this.selectedCompany}`)
				.then((response) => {
					this.moduleLists = response.data.allModules;
					this.assigned_modules = response.data.assigned_modules;
				})
				.catch(() => {})
				.finally(() => {
					this.loadingModules = false;
				});
		},
		addModules(moduleId) {
			// b-form-checkbox with a one-way :checked prop (not v-model) fires
			// @change twice per physical click. Since the backend endpoint is a
			// blind toggle (exists -> delete, missing -> create), two calls in a
			// row flipped it off then immediately back on, so the module never
			// actually changed no matter what the UI showed. Guard synchronously
			// — the :disabled re-render is too slow to block the second call.
			if (this.pendingModuleId === moduleId) return;
			this.pendingModuleId = moduleId;
			const data = new FormData();
			data.append("moduleId", moduleId);
			data.append("companyId", this.selectedCompany);
			axiosIns
				.post(`web/assign-company-module`, data)
				.then((res) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: { icon: "CoffeeIcon", variant: "success", text: res.data.message },
					});
					this.getModules();
				})
				.catch((error) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text: error.response?.data?.message || "Failed to update module",
						},
					});
				})
				.finally(() => {
					this.pendingModuleId = null;
				});
		},
		checkModule(moduleId) {
			return this.assigned_modules.some((m) => m.module_id == moduleId);
		},
		// The dropdown used to load once with no search term, which meant it
		// could only ever show the ~25 most-recently-created companies —
		// anything older (like company_id 7, from 2024) was unreachable no
		// matter what you typed, since v-select was filtering client-side
		// against that fixed list instead of asking the server. `:filterable`
		// is now off and this fires on every keystroke (debounced) so the
		// list is always a real server-side search over ALL companies.
		onCompanySearch(search, loading) {
			loading(true);
			clearTimeout(this._companySearchTimer);
			this._companySearchTimer = setTimeout(() => {
				this.getCompanyList(search).finally(() => loading(false));
			}, 300);
		},
		getCompanyList(search = "") {
			this.loadingCompanies = true;
			return axiosIns
				.get(`web/getAllCompanyList`, { params: search ? { search } : {} })
				.then((response) => {
					// getAllCompanyList returns a paginated wrapper
					// ({current_page, data: [...], ...}) — v-select needs the
					// plain array, not the wrapper itself.
					this.companyList = response.data.company_list.data;
				})
				.catch(() => {})
				.finally(() => {
					this.loadingCompanies = false;
				});
		},
		getProductionTypes() {
			this.loadingProductionTypes = true;
			axiosIns
				.get(`web/all-production-types?companyId=${this.selectedCompany}`)
				.then((response) => {
					this.productionTypeList = response.data.allProductionTypes;
					this.assigned_production_types = response.data.assigned_production_types;
				})
				.catch(() => {})
				.finally(() => {
					this.loadingProductionTypes = false;
				});
		},
		addProductionType(productionTypeId) {
			// Same double-fire guard as addModules() above — see comment there.
			if (this.pendingProductionTypeId === productionTypeId) return;
			this.pendingProductionTypeId = productionTypeId;
			const data = new FormData();
			data.append("productionTypeId", productionTypeId);
			data.append("companyId", this.selectedCompany);
			axiosIns
				.post(`web/assign-company-production-type`, data)
				.then((res) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: { icon: "CoffeeIcon", variant: "success", text: res.data.message },
					});
					this.getProductionTypes();
				})
				.catch((error) => {
					this.$toast({
						component: ToastificationContent,
						position: "top-right",
						props: {
							icon: "CoffeeIcon",
							variant: "danger",
							text: error.response?.data?.message || "Failed to update production type",
						},
					});
				})
				.finally(() => {
					this.pendingProductionTypeId = null;
				});
		},
		checkProductionType(productionTypeId) {
			return this.assigned_production_types.some((t) => t.production_type_id == productionTypeId);
		},
	},
};
</script>

<style lang="scss" scoped>
.module-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
	gap: 0.75rem;
}

.module-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.85rem 1rem;
	border: 1px solid #ebe9f1;
	border-radius: 0.5rem;
	background: #fff;
	transition: border-color 150ms ease, background-color 150ms ease;
}

.module-item--active {
	border-color: #7367f0;
	background: rgba(115, 103, 240, 0.04);
}

.module-item__icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	flex-shrink: 0;
	border-radius: 50%;
	background: rgba(115, 103, 240, 0.12);
	color: #7367f0;
}

.module-item__label {
	flex: 1;
	min-width: 0;
	font-weight: 500;
	overflow-wrap: break-word;
}

.module-item__switch {
	flex-shrink: 0;
	margin-bottom: 0;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
